import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, saveBlobAs } from "@/lib/api";
import { useProduct } from "@/lib/product-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Eye, FileText, PlusCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function DashboardPage() {
  const { meta } = useProduct();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.listSessions();
      setSessions(data);
    } catch (e) {
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this session and all its results?")) return;
    await api.deleteSession(id);
    toast.success("Session deleted");
    load();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2">
            Dashboard {meta ? `· ${meta.name} · ${meta.environment}` : ""}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            {meta?.name || "Klystic"} UAT sessions
          </h1>
          <p className="mt-2 text-slate-600 text-sm">
            Every tester's run for <strong>{meta?.name || "this product"}</strong> lives here.
            Switch product from the header to jump into the other product's results.
          </p>
        </div>
        <Link to="/new">
          <Button
            data-testid="dashboard-new-session-btn"
            className="bg-keplerio-navy hover:bg-keplerio-navy-dark text-white rounded-none h-11 px-6"
          >
            <PlusCircle size={16} className="mr-2" />
            New session
          </Button>
        </Link>
      </div>

      <div className="bg-white border border-slate-200">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading sessions…</div>
        ) : sessions.length === 0 ? (
          <div className="p-12 text-center">
            <div className="overline mb-2">No sessions yet</div>
            <p className="text-slate-600 max-w-md mx-auto text-sm">
              Kick off a UAT run to start capturing results. Multiple testers can run in
              parallel and their results will appear here.
            </p>
            <Link to="/new">
              <Button
                data-testid="dashboard-empty-start-btn"
                className="mt-6 bg-keplerio-navy hover:bg-keplerio-navy-dark text-white rounded-none h-10 px-6"
              >
                Start first session
              </Button>
            </Link>
          </div>
        ) : (
          <Table data-testid="sessions-table">
            <TableHeader>
              <TableRow className="border-b border-slate-200">
                <TableHead className="text-slate-500 overline">Tester</TableHead>
                <TableHead className="text-slate-500 overline">Environment</TableHead>
                <TableHead className="text-slate-500 overline">Browser / Device</TableHead>
                <TableHead className="text-slate-500 overline">Started</TableHead>
                <TableHead className="text-slate-500 overline">Status</TableHead>
                <TableHead className="text-slate-500 overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((s) => (
                <TableRow
                  key={s.id}
                  data-testid={`session-row-${s.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <TableCell>
                    <div className="font-medium text-slate-900">{s.tester_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{s.id.slice(0, 8)}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{s.environment}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {s.browser || "—"}
                    {s.device ? ` · ${s.device}` : ""}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 font-mono">
                    {new Date(s.started_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={s.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link to={`/session/${s.id}`}>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`view-session-${s.id}`}
                          className="rounded-none h-8 border-slate-300"
                        >
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                      </Link>
                      {s.status === "in_progress" && (
                        <Link to={`/session/${s.id}/run`}>
                          <Button
                            size="sm"
                            data-testid={`resume-session-${s.id}`}
                            className="rounded-none h-8 bg-keplerio-teal-dark hover:bg-keplerio-navy text-white"
                          >
                            Resume
                          </Button>
                        </Link>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`export-session-${s.id}`}
                        onClick={async () => {
                          try {
                            const res = await api.downloadSessionCsv(s.id);
                            saveBlobAs(res.data, `uat-session-${s.id.slice(0, 8)}.csv`);
                          } catch {
                            toast.error("Failed to export CSV");
                          }
                        }}
                        className="rounded-none h-8 border-slate-300"
                        title="Export CSV"
                      >
                        <Download size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`export-pdf-${s.id}`}
                        onClick={async () => {
                          try {
                            const res = await api.downloadSessionPdf(s.id);
                            saveBlobAs(res.data, `uat-session-${s.id.slice(0, 8)}.pdf`);
                          } catch {
                            toast.error("Failed to export PDF");
                          }
                        }}
                        className="rounded-none h-8 border-slate-300"
                        title="Export PDF"
                      >
                        <FileText size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`delete-session-${s.id}`}
                        onClick={() => handleDelete(s.id)}
                        className="rounded-none h-8 border-slate-300 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-800 border-blue-200" },
    completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    aborted: { label: "Aborted", cls: "bg-slate-100 text-slate-800 border-slate-200" },
  }[status] || { label: status, cls: "bg-slate-100 text-slate-800" };
  return (
    <Badge className={`rounded-none border ${cfg.cls} font-medium`}>{cfg.label}</Badge>
  );
}
